import type { ViewerClientSpecMap, ViewerServiceData } from '@wix/thunderbolt-symbols'
import { keyBy } from 'lodash'
import type { Logger } from '@wix/thunderbolt-becky-types'
import { VIEWER_SERVICE_EXTENSION_NAME } from '@wix/thunderbolt-symbols'

export const resolveRequiredServices = (
	clientSpecMap: ViewerClientSpecMap,
	allBuilderComponentsOnPage: Set<string>,
	logger?: Logger
): Record<string, boolean> => {
	const isEditor = process.env.PACKAGE_NAME === 'thunderbolt-ds'
	const resourcesTypeForServiceData = isEditor ? 'editor' : 'viewer'
	const resourcesTypeForBuilderComponents = isEditor ? 'editor' : 'client'
	const viewerServices = Object.values(clientSpecMap).flatMap(
		(maybeViewerAppSpecData) =>
			maybeViewerAppSpecData.components
				?.filter((maybeService) => maybeService.type === VIEWER_SERVICE_EXTENSION_NAME)
				.map((service) => (service.data as unknown) as ViewerServiceData) || []
	)
	const viewerServicesMap = keyBy(viewerServices, (viewerService: ViewerServiceData) => viewerService.packageName)

	// Process all apps in the client spec map
	const filteredBuilderComponents = Object.values(clientSpecMap).flatMap(
		(appSpecData) =>
			appSpecData.components?.filter((component) => allBuilderComponentsOnPage.has(component.data.type)) || []
	)

	const requiredServicesMap: Record<string, boolean> = {}

	const firstLevelDependencyServices = new Set<string>(
		filteredBuilderComponents.flatMap((component) => component.data.resources[resourcesTypeForBuilderComponents]?.serviceDependencies || []) ?? []
	)

	const dfs = (servicePackageName: string) => {
		if (servicePackageName in requiredServicesMap) {
			return
		}
		const service = viewerServicesMap[servicePackageName]
		if (service) {
			requiredServicesMap[servicePackageName] = true
			service[resourcesTypeForServiceData]?.dependencies?.forEach((dependency) => {
				dfs(dependency)
			})
		} else {
			logger?.warn(`Service ${servicePackageName} not found in viewer services application. This service will not be loaded.`)
		}
	}

	firstLevelDependencyServices.forEach((service) => {
		dfs(service)
	})

	return requiredServicesMap
}
